import { createRoot } from "react-dom/client";
import "./index.scss";
import App from "./App/App";
import * as api from "./shared/api-client"
import * as coreApi from "./shared/core-api-client"
import * as serviceWorker from "./serviceWorker";
import { Provider } from "react-redux";
import { store } from "./store";
import { B2CAuthProvider } from "./auth/providers/B2CAuthProvider";
import { BrowserUtils } from "@azure/msal-browser";
import history from "./shared/history";
import { SettingsProvider } from "./shared/providers/SettingsProvider";
import { initAppInsights, initOpenReplay } from "./shared/services/loggingService";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import "./shared/monkey-patches";
import { ErrorContainer } from "./shared/pages/ErrorContainer";
import { AppError } from "./store/app/types";
import { unstable_HistoryRouter as HistoryRouter, useNavigate } from "react-router-dom";
import { useCallback, type ComponentProps } from "react";
import { toRelativeUri } from "./shared/utilities";

// When MSAL completes a popup (or silent-iframe) auth flow, it briefly loads the redirect
// URI - which is this app - inside that popup/iframe.  We must not bootstrap the app there:
// doing so mounts a second B2CAuthProvider whose handleRedirectPromise() consumes the
// single-use authorization code before the parent window can read it from the popup/iframe
// URL, which is exactly what breaks popup login.  The parent extracts the response from the
// URL itself, so here we do nothing and leave the hash intact for it to read.
const isMsalAuthWindow =
    BrowserUtils.isInPopup() ||
    // A silent-iframe always lands here carrying an auth response in the URL; gating on that
    // keeps a legitimately iframe-embedded app unaffected.
    (BrowserUtils.isInIframe() && /[?#].*\b(code|state|error|id_token)=/.test(window.location.href));

!isMsalAuthWindow && new api.SettingsClient().getClientSettings().then(globalSettings => {
    const domainName = globalSettings.explicitDomain ?? window.location.hostname;

    new coreApi.SettingsClient(globalSettings.coreApiUrl).getClientSettings(domainName).then(settings => {
        // Initialize logging services...
        initAppInsights(settings.appInsightsInstrumentationKey, settings.tenantName);
        settings.openReplay?.isEnabled && initOpenReplay(settings.tenantName, settings.openReplay.projectKey, settings.openReplay.ingestPoint, settings.openReplay.extraOptionsJson);

        const AppWrapper = () => {
            return (
                <HistoryRouter history={history as unknown as ComponentProps<typeof HistoryRouter>["history"]}>
                    <AppComponent />
                </HistoryRouter>
            );
        };

        const AppComponent = () => {
            const navigate = useNavigate();

            // Callback function to handle redirection after login...
            const onRedirectCallback = useCallback((appState: any) => {
                navigate(
                    toRelativeUri(
                        appState && appState.targetUrl ?
                            appState.targetUrl :
                            window.location.pathname
                    )
                );
            }, [navigate]);

            return (
                <SettingsProvider settings={{ ...globalSettings, ...settings, domainName }}>
                    <B2CAuthProvider
                        clientId={globalSettings.auth.clientId}
                        domain={globalSettings.auth.domain}
                        instance={globalSettings.auth.instance}
                        tenantId={globalSettings.auth.tenantId}
                        redirectUri={globalSettings.auth.redirectUri}
                        apiVersion={globalSettings.apiVersion}
                        environment={globalSettings.environment}
                        onRedirectCallback={onRedirectCallback}
                        appDomain={domainName}
                    >
                        <Provider store={store}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <App />
                            </LocalizationProvider>
                        </Provider>
                    </B2CAuthProvider>
                </SettingsProvider>
            );
        };

        createRoot(document.getElementById("root")).render(<AppWrapper />);
    }).catch(ex => {
        const getError = (ex): AppError => {
            if (ex.status == null)
                return { type: "Connectivity", message: `The application was unable to load tenant setting from the Core API`, details: `Unable to contact the server at \`${globalSettings.coreApiUrl}\`.\n\nPlease ensure you are connected to your corporate network or VPN.\n\nIf the issue persists, please contact the site administrator.` };

            if (ex.status === 404)
                return { type: "NotFound", message: "Tenant not found.", details: `No tenant is configured for this domain (\`${domainName}\`).\n\nPlease check the address and try again.` };

            if (ex.status === 429)
                return { type: "Connectivity", message: "Rate limit exceeded.", details: `Too many requests were submitted within the last minute.\n\nPlease wait a moment and try again.` };

            return { type: "Generic", message: "An error occurred while loading tenant settings from the Core API.", details: ex.response };
        };

        createRoot(document.getElementById("root")).render(<ErrorContainer error={getError(ex)} />);
    });
}).catch(ex => {
    const hostName = window.location.hostname;

    const getError = (ex): AppError => {
        if (ex.status == null)
            return { type: "Connectivity", message: `Your browser cannot reach the Web Application.`, details: `Unable to contact the server at \`${hostName}\`.\n\nPlease ensure you are connected to your corporate network or VPN.\n\nIf the issue persists, please contact the site administrator.` };

        if (ex.status === 429)
            return { type: "Connectivity", message: "Rate limit exceeded.", details: `Too many requests were submitted within the last minute.\n\nPlease wait a moment and try again.` };

        return { type: "Generic", message: "An error occurred while loading tenant settings from the Web Application.", details: ex.response };
    };

    createRoot(document.getElementById("root")).render(<ErrorContainer error={getError(ex)} />);
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
